import { z } from 'zod';
import { LabelIdSchema, TenantIdSchema } from './schemas';
import { NfcTagUidSchema } from './labelNfc';
import { ClientDeviceLocationSchema } from './labelLocation';

/**
 * Contrato do callable `bindNfcTag` — vincula a tag NFC recém-gravada à
 * etiqueta, e emite a etiqueta só digital.
 *
 * Origem: #900/#901 — 2026-10-09 15:10 +0200 (módulo Etiquetas inteligentes).
 *
 * Por quê: a gravação física acontece no aparelho (o chip leva o `labelId`);
 * o servidor registra QUAL chip carrega QUAL etiqueta. É isso que permite
 * detectar tag regravada fora do app (decisão D3: tag sem proteção) e
 * resolver uma tag apagada pelo UID.
 *
 * Dados — input (callable `bindNfcTag`, região `southamerica-east1`):
 *   { "tenantId": "tenant-1", "labelId": "AbCdEfGhIjKlMnOpQrSt", "tagUid": "04A1B2C3D4E5F6" }
 * output:
 *   { "labelId": "AbCdEfGhIjKlMnOpQrSt", "promoted": true,
 *     "previousLabelId": null, "writeCount": 1 }
 *  - `promoted`: a etiqueta era `DRAFT` e virou `PRINTED` (`via: NFC`) — é
 *    a etiqueta só digital, emitida sem imprimir.
 *  - `previousLabelId`: outra etiqueta do tenant que tinha esta tag e foi
 *    desvinculada (tag reaproveitada), ou `null`.
 * Recusas (`HttpsError`, `details.reason` — os clientes casam por este texto,
 * ver `BindNfcTagRefusal`): `module_inactive` (`failed-precondition`, vem do
 * `requireModule`), `label_not_found` (`not-found`); `failed-precondition`
 * com `label_not_active` (não é `DRAFT` nem `PRINTED`), `label_no_validity`
 * e `label_expired` (rascunho que não pode virar vigente), `stale_bind`
 * (vínculo mais novo já gravado — auditoria 2026-10-09, #900).
 *
 * Usado por: `apps/functions/src/nfc/bindNfcTag.ts` (parse da borda); Android
 * `FirebaseNfcTagRepository` (espelho manual dos campos); iOS
 * `Data/Sources/Nfc/FirebaseNfcTagRepository.swift` (#905, espelho manual).
 *
 * Dicas: o `tagUid` chega normalizado pelo cliente (14 hex maiúsculos); o
 * schema recusa qualquer outra forma em vez de corrigi-la, para um cliente com
 * bug de formatação aparecer como `invalid-argument` e não como vínculo
 * duplicado do mesmo chip.
 *
 * Issues: #900, #901.
 */

/** Ids que viram caminho de doc: sem `/` (mesma trava do #664, servidor M4). */
const DOC_ID = /^[A-Za-z0-9_-]{1,128}$/;

/**
 * Entrada do callable `bindNfcTag`: tenant, etiqueta, UID do chip e a hora da
 * gravação no aparelho.
 *
 * Origem: #900/#901 — 2026-10-09 15:10 +0200.
 *
 * Contrato: `tagUid` = 14 hex maiúsculos (UID de 7 B); ids sem `/` (viraria
 * caminho de subcoleção); `clientWrittenAtMillis` ordena gravações do mesmo
 * chip — o servidor o corta em `now + 5 min` (relógio adiantado, #900).
 *
 * Usado por: `apps/functions/src/nfc/bindNfcTag.ts` (parse da borda); espelho
 * manual no Android (`FirebaseNfcTagRepository.bind`) e no iOS
 * (`FirebaseNfcTagRepository.bind`, #905).
 */
export const BindNfcTagInputSchema = z.object({
  tenantId: TenantIdSchema.refine((s) => DOC_ID.test(s), {
    message: 'Identificador de estabelecimento inválido.',
  }),
  labelId: LabelIdSchema.refine((s) => DOC_ID.test(s), {
    message: 'Identificador de etiqueta inválido.',
  }),
  tagUid: NfcTagUidSchema,
  /**
   * Hora da gravação no aparelho (`TrustedClock`), auditoria 2026-10-09 (#900,
   * A1): o servidor recusa (`stale_bind`) o vínculo mais antigo que o atual do
   * mesmo chip ou da mesma etiqueta — o reenvio da fila offline não desfaz uma
   * regravação feita depois. Opcional para cliente anterior ao campo (= 0).
   */
  clientWrittenAtMillis: z.number().int().nonnegative().optional(),
  /**
   * Posição do aparelho na gravação (#996, 2026-10-18 14:17 +0200). O cliente
   * bloqueia a gravação sem posição; o schema a deixa opcional porque builds
   * anteriores ao #996 não a mandam e o vínculo deles continua valendo. O
   * servidor grava em `labels/{id}.nfcLocation` com `capturedVia: NFC`.
   */
  location: ClientDeviceLocationSchema.optional(),
});
export type BindNfcTagInput = z.infer<typeof BindNfcTagInputSchema>;

export const BindNfcTagOutputSchema = z.object({
  labelId: z.string().min(1),
  promoted: z.boolean(),
  previousLabelId: z.string().min(1).nullable(),
  writeCount: z.number().int().positive(),
});
export type BindNfcTagOutput = z.infer<typeof BindNfcTagOutputSchema>;

/**
 * `details.reason` das recusas do `bindNfcTag` (além do `module_inactive`).
 *
 * Origem: #900/#901 — 2026-10-09 15:10 +0200; `stale_bind`, `label_no_validity`
 * e `label_expired` vieram da auditoria do mesmo dia (A1, M1).
 *
 * Usado por: `bindNfcTag` (servidor, `planNfcBind`); Android
 * (`FirebaseNfcTagRepository.mapFailure` casa pelo texto — renomear quebra o
 * cliente publicado); iOS `FirebaseNfcTagRepository.preconditionOutcome` (#905,
 * casa pelo mesmo texto).
 */
export const BindNfcTagRefusal = {
  LABEL_NOT_FOUND: 'label_not_found',
  LABEL_NOT_ACTIVE: 'label_not_active',
  /** Auditoria 2026-10-09 (#900, M1): rascunho sem regra de validade não vira vigente pela tag. */
  LABEL_NO_VALIDITY: 'label_no_validity',
  /** Auditoria 2026-10-09 (#900, M1): rascunho já vencido não vira vigente pela tag. */
  LABEL_EXPIRED: 'label_expired',
  /** Auditoria 2026-10-09 (#900, A1): já existe vínculo mais novo para o chip ou para a etiqueta. */
  STALE_BIND: 'stale_bind',
} as const;
export type BindNfcTagRefusal = (typeof BindNfcTagRefusal)[keyof typeof BindNfcTagRefusal];
